// Lógica de armado de datos para los gráficos "Energía Eléctrica Evolución
// de Consumos" y "Energía Eléctrica Intensidad" de EnergyKPIsDashBoard.js,
// extraída a un módulo aparte (sin JSX/hooks) para poder reusarla también
// desde el export a PDF (pdfEnergyCharts.js) sin duplicar la lógica ni
// crear un import circular con EnergyKPIsDashBoard.js.

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Lee los colores por año (más viejo/anterior/último) desde variables CSS —
// funciona igual dentro o fuera de un componente React, no depende de hooks.
function buildYearColorMap(years) {
  const getCssVar = (name) =>
    getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const chartColors = {
    oldest: getCssVar("--chart-year-oldest"),
    prev: getCssVar("--chart-year-prev"),
    last: getCssVar("--chart-year-last"),
  };
  const sorted = [...years].map(Number).sort((a, b) => a - b);
  return {
    [sorted[0]]: chartColors.oldest || "#cbd5e1", // Fallback si el CSS no carga
    [sorted[1]]: chartColors.prev || "#94a3b8",
    [sorted[2]]: chartColors.last || "#1e293b",
  };
}

// Escala Y explícita y compartida entre dos series (barra+línea o
// línea+línea): "sync" por sí solo no las alineaba correctamente.
export function buildSharedYScale(...dataArrays) {
  const combinedMax = Math.max(
    0,
    ...dataArrays.flatMap((arr) => arr.map((d) => d.value || 0)),
  );
  const yDomainMax = combinedMax > 0 ? Math.ceil(combinedMax * 1.15) : undefined;
  return { domainMin: 0, domainMax: yDomainMax, nice: true };
}

// Arma barData/lineData/yearColorMap para "Energía Eléctrica Evolución de
// Consumos" — misma lógica que usaba el useMemo original de EnergyComparisonChart.
export function buildEnergyComparisonData(resultsByYear, t) {
  const years = Object.keys(resultsByYear);
  const bar = [];
  const line = [];
  const sortedYears = [...years].sort((a, b) => parseInt(b) - parseInt(a));

  sortedYears.forEach((year) => {
    const values = months.map(
      (month) => resultsByYear?.[year]?.[month]?.electrica?.total || 0,
    );
    const avg =
      values.reduce((a, b) => a + b, 0) /
      (values.filter((v) => v > 0).length || 1);

    months.forEach((month, i) => {
      bar.push({
        month: t(month),
        value: values[i],
        year: String(year), // 👈 Usado para leyenda y color
        tipo: "Consumo", // 👈 Usado para el tooltip
      });
      line.push({
        month: t(month),
        value: avg,
        year: String(year),
        tipo: "Promedio",
      });
    });
  });

  return { years, barData: bar, lineData: line, yearColorMap: buildYearColorMap(years) };
}

// Arma consumptionData/averageData/yearColorMap para "Energía Eléctrica
// Intensidad" — misma lógica que usaba el useMemo original de EnergyMonthlyChart.
export function buildEnergyMonthlyData(resultsByYear, resultsProductions, t) {
  const years = Object.keys(resultsByYear);
  const cData = [];
  const aData = [];

  const sortedYears = [...years].sort((a, b) => parseInt(b) - parseInt(a));

  sortedYears.forEach((year) => {
    const totals = months.map((m) => {
      const energy = resultsByYear?.[year]?.[m]?.electrica?.total ?? null;
      const production = resultsProductions?.[year]?.[m] ?? null;
      if (!energy || !production || production === 0) {
        return null; // 🔹 no dibuja caída a cero
      }
      return energy / production;
    });

    // 🔹 Filtrar solo valores válidos
    const validValues = totals.filter((v) => v !== null && !isNaN(v));
    const monthsWithData = validValues.length || 1;
    const avg = validValues.reduce((a, b) => a + b, 0) / monthsWithData;

    months.forEach((month, i) => {
      cData.push({
        year: String(year),
        month: t(month),
        value: totals[i],
        tipo: "Consumo",
        type: `Consumo ${year}`,
      });
      aData.push({
        year: String(year),
        month: t(month),
        value: validValues.length > 0 ? avg : null,
        tipo: "Promedio",
        type: `Promedio ${year}`,
      });
    });
  });

  return { years, consumptionData: cData, averageData: aData, yearColorMap: buildYearColorMap(years) };
}
